import React, { useState } from 'react';
import css from './MaterialDetails.module.css';
import IconCard from '../SavedCardDetails/IconCard/IconCard';
import { categoryLabel } from '../../util/categoryLabels';

const EXCLUDED_PUB_KEYS = new Set([
  'stock', 'condition', 'listingType', 'transactionProcessAlias', 'unitType',
  'categoryLevel', 'categoryLevel1', 'categoryLevel2', 'categoryLevel3',
  'categoryLevel4', 'categoryLevel5', 'subCategoryLevel', 'title', 'description',
  'installer_or_listing', 'availability', 'preSaleDate', 'isFeatured',
  'is_featuredweek', 'serviceLocations', 'subscriptionPlan', 'subscriptionAmount',
  'isSubscriptionPlan', 'pub_stock', 'requiredFieldsArray', 'optionalFieldsArray',
]);

const MaterialDetails = ({ publicData, stockLoading = false, categories }) => {
  const [expanded, setExpanded] = useState(false);

  const hasValue = (value) => {
    if (value == null) return false;
    if (typeof value === 'string' && value.trim() === '') return false;
    if (Array.isArray(value) && value.length === 0) return false;
    if (typeof value === 'object' && !Array.isArray(value) && Object.keys(value).length === 0) return false;
    return true;
  };

  const formatKeyLabel = key => {
    if (!key) return '';
    const SMALL_WORDS = new Set(['a', 'an', 'and', 'as', 'at', 'but', 'by', 'for', 'from', 'in', 'nor', 'of', 'on', 'or', 'per', 'the', 'to', 'with']);
    let processedKey = String(key).replace(/^pub_/i, '').replace(/^pub([A-Z])/i, '$1');
    return processedKey
      .replace(/_/g, ' ')
      .replace(/([a-z])([A-Z])/g, '$1 $2')
      .replace(/(\D)(\d+)/g, '$1 $2')
      .replace(/\s+/g, ' ')
      .trim()
      .split(' ')
      .filter(Boolean)
      .map((w, i) => {
        const lower = w.toLowerCase();
        if (i > 0 && SMALL_WORDS.has(lower)) return lower;
        return lower.charAt(0).toUpperCase() + lower.slice(1);
      })
      .join(' ');
  };

  const formatValue = value => {
    if (value == null) return '';
    if (typeof value === 'boolean') return value ? 'Yes' : 'No';
    if (typeof value === 'string') {
      const v = value.trim();
      if (v && v.includes('_') && !v.includes(' ')) {
        return v.split('_').filter(Boolean).map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
      }
      if (/^[a-z]+(?: [a-z]+)*$/.test(v)) {
        return v.split(' ').filter(Boolean).map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
      }
      return v;
    }
    if (Array.isArray(value)) {
      return value.map(v => (v == null ? '' : typeof v === 'object' ? JSON.stringify(v) : formatValue(v))).filter(Boolean).join(', ');
    }
    if (typeof value === 'object') return JSON.stringify(value);
    return String(value);
  };

  const display = v => formatValue(v) || '-';
  const isInchesLabel = label => /inch|inches|\(in\b|,\s*in\b/i.test(String(label || ''));
  const isWeightCapacityField = (key, label) =>
    /weight.*capacity|capacity.*weight/i.test(`${key || ''} ${label || ''}`);

  const displayPoundsValue = value => {
    const formatted = display(value);
    if (formatted === '-') return formatted;
    if (/unknown|other|n\/a/i.test(formatted)) return formatted;

    const withoutWrongUnits = formatted
      .replace(/["”]/g, '')
      .replace(/\b(?:inches|inch)\b/gi, '')
      .replace(/\bpounds?\b/gi, 'lbs')
      .replace(/\s+/g, ' ')
      .trim();

    return /\blbs?\b/i.test(withoutWrongUnits)
      ? withoutWrongUnits.replace(/\blbs?\b/gi, 'lbs')
      : `${withoutWrongUnits} lbs`;
  };

  const displayFieldValue = (value, label, key) => {
    if (isWeightCapacityField(key, label)) return displayPoundsValue(value);

    const formatted = display(value);
    if (!isInchesLabel(label) || formatted === '-') return formatted;
    if (/["”]|unknown|other|n\/a/i.test(formatted)) return formatted;
    return /^-?\d+(?:\.\d+)?$/.test(formatted) ? `${formatted}"` : formatted;
  };

  const normalizeFieldId = value =>
    String(value || '')
      .replace(/^pub_/i, '')
      .replace(/\*/g, '')
      .replace(/\([^)]*\)/g, '')
      .replace(/[^a-z0-9]+/gi, '_')
      .replace(/^_+|_+$/g, '')
      .toLowerCase();

  const getFieldKey = field =>
    field?.key || (field?.label && String(field.label).replace(/\s+/g, '_').toLowerCase());

  const dimensionKinds = {
    length: new Set(['length', 'beam_length', 'deck_length']),
    width: new Set(['width', 'beam_width', 'deck_width', 'frame_width']),
    height: new Set(['height', 'beam_height', 'frame_height']),
    depth: new Set(['depth', 'beam_depth', 'deck_depth', 'frame_depth']),
  };

  const getPrimaryDimensionKind = field => {
    const fieldKey = getFieldKey(field);
    const ids = [
      normalizeFieldId(fieldKey),
      normalizeFieldId(field?.label),
    ].filter(Boolean);

    return Object.entries(dimensionKinds).find(([, aliases]) =>
      ids.some(id => aliases.has(id))
    )?.[0];
  };

  const safePublicData = publicData && typeof publicData === 'object' ? publicData : {};

  // Category display: categoryLevel2 = "Category", categoryLevel3 = "Subcategory".
  // Show both (mirroring the edit-listing form) so the subcategory isn't mislabeled
  // as the category. Collapse to a single line only when the two resolve to the exact
  // same text (true duplication) — not just because it's a complete-system subcategory.
  // Fall back to the taxonomy's own name rather than humanizing the id, since
  // some ids carry typos (e.g. complete_rush_back renders "Complete Rush Back"
  // where the taxonomy name is "Complete Push Back Racking System"). Listings
  // saved before categoryLevel/subCategoryLevel were persisted rely on this.
  const categoryLabelValue = hasValue(safePublicData.categoryLevel)
    ? safePublicData.categoryLevel
    : categoryLabel(safePublicData.categoryLevel2, categories, display, [
        safePublicData.categoryLevel1,
      ]);
  const subCategoryLabelValue = hasValue(safePublicData.subCategoryLevel)
    ? safePublicData.subCategoryLevel
    : categoryLabel(safePublicData.categoryLevel3, categories, display, [
        safePublicData.categoryLevel1,
        safePublicData.categoryLevel2,
      ]);
  const hasCategory = hasValue(safePublicData.categoryLevel2);
  const hasSubCategory = hasValue(safePublicData.categoryLevel3);
  const normalizeLabel = s => String(s || '').toLowerCase().replace(/[^a-z0-9]+/g, '');
  const categoryEqualsSubCategory =
    hasCategory && hasSubCategory &&
    normalizeLabel(categoryLabelValue) === normalizeLabel(subCategoryLabelValue);

  const getOtherValue = (fieldName) => {
    const snakeCaseOther = `pub_${fieldName}_other`;
    const camelCaseOther = `pub${fieldName.charAt(0).toUpperCase() + fieldName.slice(1)}Other`;
    return safePublicData[snakeCaseOther] || safePublicData[camelCaseOther];
  };

  // Quantity is already shown at the top (from pub_stock). A "Quantity" field
  // also comes through in requiredFieldsArray/optionalFieldsArray, which would
  // render it a second time — drop it from those loops when the top one shows.
  const QUANTITY_FIELD_IDS = new Set(['quantity', 'stock', 'pub_stock', 'stock_quantity']);
  const showsTopQuantity = hasValue(safePublicData.pub_stock);
  const isQuantityField = field =>
    [getFieldKey(field), field?.label]
      .map(normalizeFieldId)
      .filter(Boolean)
      .some(id => QUANTITY_FIELD_IDS.has(id));

  const requiredFieldsArray = (Array.isArray(safePublicData.requiredFieldsArray)
    ? safePublicData.requiredFieldsArray : []
  ).filter(f => !(showsTopQuantity && isQuantityField(f)));

  const rawOptional = Array.isArray(safePublicData.optionalFieldsArray)
    ? safePublicData.optionalFieldsArray : [];

  // Always merge optionalFieldsArray with pub_* fallback (dedup by key)
  const pubFallback = Object.entries(safePublicData)
    .filter(([k, v]) => {
      if (!k.startsWith('pub_')) return false;
      const bareKey = k.replace(/^pub_/, '');
      if (EXCLUDED_PUB_KEYS.has(k) || EXCLUDED_PUB_KEYS.has(bareKey)) return false;
      if (k.endsWith('_other')) return false;
      return hasValue(v);
    })
    .map(([k, v]) => ({ key: k.replace(/^pub_/, ''), value: v }));

  const rawOptionalKeys = new Set(rawOptional.map(f => f.key));
  const optionalFieldsArray = [
    ...rawOptional,
    ...pubFallback.filter(f => !rawOptionalKeys.has(f.key)),
  ];

  const requiredFieldIds = new Set(
    requiredFieldsArray
      .flatMap(field => [getFieldKey(field), field?.label])
      .map(normalizeFieldId)
      .filter(Boolean)
  );

  const requiredDimensionKinds = new Set(
    requiredFieldsArray
      .map(getPrimaryDimensionKind)
      .filter(Boolean)
  );

  const isRepeatedOptionalField = field => {
    const fieldIds = [getFieldKey(field), field?.label].map(normalizeFieldId).filter(Boolean);
    if (fieldIds.some(id => requiredFieldIds.has(id))) return true;

    const dimensionKind = getPrimaryDimensionKind(field);
    return dimensionKind && requiredDimensionKinds.has(dimensionKind);
  };

  const visibleOptional = optionalFieldsArray.filter(
    f =>
      hasValue(f?.value) &&
      !isRepeatedOptionalField(f) &&
      !(showsTopQuantity && isQuantityField(f))
  );

  return (
    <div className={css.wrapper}>
      <div className={`${css.card} ${expanded ? css.expanded : ''}`}>
        {hasValue(safePublicData.title) && (
          <h4 className={css.componentTitle}>{safePublicData.title}</h4>
        )}
        <h3 className={css.title}>Material Description</h3>

        <div className={css.grid}>
          {(hasCategory || hasSubCategory) && (
            <div className={css.mobileFlex}>
              <p className={css.label}>Material Type</p>
              <div>
                {/* Category line: the parent category, or the subcategory when there's
                    no parent (so we never leave the row blank). */}
                {hasCategory ? (
                  <p>Category: {categoryLabelValue}</p>
                ) : (
                  <p>Category: {subCategoryLabelValue}</p>
                )}
                {/* Sub-category line: shown whenever a distinct subcategory exists —
                    collapsed only when it's the exact same text as the category. */}
                {hasCategory && hasSubCategory && !categoryEqualsSubCategory && (
                  <p>Sub-category: {subCategoryLabelValue}</p>
                )}
              </div>
            </div>
          )}
          {hasValue(safePublicData.pub_stock) && (
            <div className={css.mobileFlex}>
              <p className={css.label}>Quantity</p>
              {/* While the fresh listing fetch is in flight the cached entity
                  may carry a stale quantity — show a skeleton instead of
                  flashing the old number. */}
              {stockLoading ? (
                <div><span className={css.qtySkeleton} aria-hidden="true" /></div>
              ) : (
                <div><p>{display(safePublicData.pub_stock)}</p></div>
              )}
            </div>
          )}
          {hasValue(safePublicData.unitType) && (
            <div className={css.mobileFlex}>
              <p className={css.label}>Unit Type</p>
              <div><p>{display(safePublicData.unitType)}</p></div>
            </div>
          )}
          {requiredFieldsArray.length > 0 && requiredFieldsArray.map(field => {
            const { key, label, value } = field || {};
            const fieldKey = key || (label && String(label).replace(/\s+/g, '_').toLowerCase());
            if (!fieldKey && !label) return null;
            const otherValue = getOtherValue(fieldKey);
            return (
              <div key={fieldKey} className={css.mobileFlex}>
                <p className={css.label}>{label || formatKeyLabel(fieldKey)}</p>
                <div>
                  <p>{displayFieldValue(value, label, fieldKey)}</p>
                  {hasValue(otherValue) && <p>{displayFieldValue(otherValue, label, fieldKey)}</p>}
                </div>
              </div>
            );
          })}

          {expanded && visibleOptional.length > 0 && (
            <>
              <div className={css.sectionTitle}>Other Specifications</div>
              {visibleOptional.map(field => {
                const { key, label, value } = field || {};
                const fieldKey = key || (label && String(label).replace(/\s+/g, '_').toLowerCase());
                if (!fieldKey && !label) return null;
                const otherValue = getOtherValue(fieldKey);
                return (
                  <div key={fieldKey} className={css.mobileFlex}>
                    <p className={css.label}>{label || formatKeyLabel(fieldKey)}</p>
                    <div>
                      <p>{displayFieldValue(value, label, fieldKey)}</p>
                      {hasValue(otherValue) && <p>{displayFieldValue(otherValue, label, fieldKey)}</p>}
                    </div>
                  </div>
                );
              })}
            </>
          )}
        </div>
      </div>

      <button className={css.toggleBtn} onClick={() => setExpanded(!expanded)}>
        {visibleOptional.length > 0 ? (
          <>
            <span className={css.icon}>
              {expanded ? <IconCard brand="see-up" /> : <IconCard brand="see-down" />}
            </span>
            {expanded ? 'See less' : 'See more'}
          </>
        ) : null}
      </button>
    </div>
  );
};

export default MaterialDetails;
